/**
 * A dialog component for warning users about unsaved form changes.
 *
 * This component is designed to be controlled via a ref, exposing imperative methods
 * to initialize, open, and close the dialog. It displays a customizable title, message,
 * and action buttons.
 *
 * @component
 * @example
 * const dialogRef = useRef();
 *
 * // To show the dialog:
 * dialogRef.current.init({
 *   title: 'Warning',
 *   message: 'You have unsaved changes. Are you sure you want to leave?',
 *   buttons: [
 *     { key: 'cancel', label: 'Cancel', onClick: handleCancel },
 *     { key: 'leave', label: 'Leave', onClick: handleLeave, color: 'error' }
 *   ]
 * });
 * dialogRef.current.open();
 *
 * @param {object} props - Component props (not used directly).
 * @param {React.Ref} ref - Ref to control the dialog imperatively.
 *
 * @returns {JSX.Element|null} The dialog element, or null if not open.
 *
 * @typedef {Object} DialogButton
 * @property {string} key - Unique key for the button.
 * @property {string} label - Button label.
 * @property {function} onClick - Click handler for the button.
 * @property {string} [variant] - MUI Button variant (e.g., 'contained').
 * @property {string} [color] - MUI Button color (e.g., 'primary', 'error').
 *
 * @typedef {Object} DialogOptions
 * @property {string} [title] - Dialog title.
 * @property {string|JSX.Element} [message] - Dialog message content.
 * @property {number} [width=360] - Dialog width in pixels.
 * @property {DialogButton[]} [buttons] - Array of action buttons.
 * @property {string} [size='medium'] - Button size.
 *
 * @see {@link https://mui.com/material-ui/react-dialog/|MUI Dialog}
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import { DialogTitle, Stack } from '@mui/material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';

const FormActionDialog = forwardRef((props, ref) => {
  const prevOptions = useRef();
  const defaultOptions = {
    title: null,
    Icon: InfoOutlineIcon,
    message: null,
    width: 400,
    buttons: [],
    size: 'medium',
  };

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(defaultOptions);

  useImperativeHandle(ref, () => ({
    init(popupOptions) {
      setOptions({ ...defaultOptions, ...popupOptions });
    },
    open() {
      setOpen(true);
    },
    close() {
      setOpen(false);
    },
    updateMessage(newMessage) {
      setOptions({ ...prevOptions.current, message: newMessage });
    },
  }));

  useEffect(() => {
    prevOptions.current = options;
  }, [options]);

  if (!open) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Dialog
        open={open}
        sx={{
          '& .MuiDialog-paper': {
            width: `${options?.width || defaultOptions?.width}px`,
            maxHeight: 435,
          },
        }}>
        <DialogTitle sx={{ padding: '8px 20px 20px', fontSize: 18 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {options?.Icon ? (
              <options.Icon sx={{ mr: 0.5 }} />
            ) : (
              <InfoOutlineIcon sx={{ mr: 0.5 }} />
            )}
            {options.title || 'Unsaved Changes'}
          </Box>
        </DialogTitle>

        <DialogContent>{options?.message}</DialogContent>

        {options.buttons.length > 0 && (
          <DialogActions sx={{ mb: 1, justifyContent: 'center' }}>
            <Stack direction="row" spacing={1}>
              {options.buttons.map(button => {
                return (
                  <Button
                    {...(button.Icon && { startIcon: <button.Icon /> })}
                    key={button?.label?.props?.id || button?.label}
                    size={options?.size || defaultOptions?.size}
                    variant={button.variant ?? 'contained'}
                    color={button.color ?? 'primary'}
                    type="button"
                    onClick={() => button.onClick()}>
                    {button.label}
                  </Button>
                );
              })}
            </Stack>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
});

export default FormActionDialog;
